// Mock data for LÍDER IA prototype

export type UserRole = 'participant' | 'mentor' | 'admin' | 'super_admin' | null;

export interface Organization {
  id: string;
  name: string;
  logo: string;
  participantsCount: number;
  mentorsCount: number;
  avgProgress: number;
  demodayReady: number;
}

export const mockOrganizations: Organization[] = [
  { id: 'org1', name: 'TechSolutions MX', logo: '🏢', participantsCount: 15, mentorsCount: 2, avgProgress: 62, demodayReady: 4 },
  { id: 'org2', name: 'DataDriven Co', logo: '📊', participantsCount: 12, mentorsCount: 2, avgProgress: 55, demodayReady: 3 },
  { id: 'org3', name: 'InnovateMX', logo: '🚀', participantsCount: 8, mentorsCount: 1, avgProgress: 71, demodayReady: 3 },
  { id: 'org4', name: 'FinTech Global', logo: '💳', participantsCount: 13, mentorsCount: 2, avgProgress: 48, demodayReady: 2 },
];

export type DeliverableStatus = 'not_started' | 'in_progress' | 'submitted' | 'validated' | 'needs_revision';

export type SolutionType = 1 | 2 | 3 | 4 | 5;

export const SOLUTION_TYPES = {
  1: { 
    name: 'Creación de Contenido con IA', 
    icon: '✍️',
    description: 'Genera contenido de valor usando herramientas de IA',
    color: 'level-1'
  },
  2: { 
    name: 'Vibe Coding', 
    icon: '💻',
    description: 'Crea soluciones y aplicaciones con IA',
    color: 'level-2'
  },
  3: { 
    name: 'Asistentes o Agentes Inteligentes', 
    icon: '🤖',
    description: 'Crea asistentes que automaticen interacciones',
    color: 'level-3'
  },
  4: { 
    name: 'Automatizaciones', 
    icon: '⚙️',
    description: 'Automatiza procesos con herramientas no-code',
    color: 'level-4'
  },
  5: { 
    name: 'Análisis de Datos con IA', 
    icon: '📊',
    description: 'Analiza y visualiza datos para tomar mejores decisiones',
    color: 'level-5'
  },
} as const;

export const TOOLS = [
  { id: 'chatgpt', name: 'ChatGPT', category: 'IA', icon: '🤖' },
  { id: 'claude', name: 'Claude', category: 'IA', icon: '🧠' },
  { id: 'midjourney', name: 'Midjourney', category: 'IA', icon: '🎨' },
  { id: 'powerbi', name: 'Power BI', category: 'Análisis', icon: '📊' },
  { id: 'looker', name: 'Looker', category: 'Análisis', icon: '📈' },
  { id: 'notion', name: 'Notion', category: 'Productividad', icon: '📝' },
  { id: 'make', name: 'Make', category: 'Automatización', icon: '⚙️' },
  { id: 'n8n', name: 'n8n', category: 'Automatización', icon: '🔄' },
  { id: 'zapier', name: 'Zapier', category: 'Automatización', icon: '⚡' },
  { id: 'softr', name: 'Softr', category: 'Frontend', icon: '🖥️' },
  { id: 'lovable', name: 'Lovable', category: 'Vibe Coding', icon: '💜' },
];

export interface Deliverable {
  id: string;
  name: string;
  description: string;
  solutionType: SolutionType;
  status: DeliverableStatus;
  toolUsed?: string;
  challengeRelation: string;
  submittedAt?: string;
  feedback?: string;
}

export interface Participant {
  id: string;
  name: string;
  email: string;
  company: string;
  avatar: string;
  challenge: string;
  mentorId: string;
  progress: number;
  currentSolutionType: SolutionType;
  deliverables: Deliverable[];
  badges: string[];
  streak: number;
  daysUntilDemoday: number;
  canAccessDemoday: boolean;
}

export interface Mentor {
  id: string;
  name: string;
  email: string;
  avatar: string;
  participantIds: string[];
  pendingValidations: number;
}

export const mockParticipants: Participant[] = [
  {
    id: 'p1',
    name: 'María García',
    email: 'maria@empresa.com',
    company: 'TechSolutions MX',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Maria',
    challenge: 'Reducir el tiempo de respuesta al cliente en un 50% usando automatización inteligente',
    mentorId: 'm1',
    progress: 65,
    currentSolutionType: 3,
    badges: ['early_bird', 'first_delivery', 'content_master'],
    streak: 5,
    daysUntilDemoday: 12,
    canAccessDemoday: false,
    deliverables: [
      {
        id: 'd1',
        name: 'Manual de respuestas frecuentes',
        description: 'Documento con las 50 preguntas más frecuentes y sus respuestas optimizadas',
        solutionType: 1,
        status: 'validated',
        toolUsed: 'chatgpt',
        challengeRelation: 'Base de conocimiento para el asistente',
        submittedAt: '2024-01-15',
      },
      {
        id: 'd2',
        name: 'Análisis de tickets de soporte',
        description: 'Dashboard con métricas de tiempos de respuesta y categorías de consultas',
        solutionType: 2,
        status: 'validated',
        toolUsed: 'powerbi',
        challengeRelation: 'Identificar áreas de mejora en atención',
        submittedAt: '2024-01-20',
      },
      {
        id: 'd3',
        name: 'Chatbot de atención inicial',
        description: 'Asistente que responde las 20 preguntas más frecuentes',
        solutionType: 3,
        status: 'in_progress',
        toolUsed: 'chatgpt',
        challengeRelation: 'Automatizar respuestas de primer nivel',
      },
    ],
  },
  {
    id: 'p2',
    name: 'Carlos López',
    email: 'carlos@startup.io',
    company: 'DataDriven Co',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Carlos',
    challenge: 'Predecir demanda de productos usando IA para optimizar inventario',
    mentorId: 'm1',
    progress: 40,
    currentSolutionType: 2,
    badges: ['early_bird'],
    streak: 2,
    daysUntilDemoday: 12,
    canAccessDemoday: false,
    deliverables: [
      {
        id: 'd4',
        name: 'Reporte de tendencias de mercado',
        description: 'Análisis de tendencias del último año generado con IA',
        solutionType: 1,
        status: 'validated',
        toolUsed: 'claude',
        challengeRelation: 'Contexto para el modelo predictivo',
        submittedAt: '2024-01-18',
      },
      {
        id: 'd5',
        name: 'Dashboard de ventas históricas',
        description: 'Visualización de patrones de demanda',
        solutionType: 2,
        status: 'submitted',
        toolUsed: 'looker',
        challengeRelation: 'Datos para entrenar predicción',
        submittedAt: '2024-01-22',
      },
    ],
  },
  {
    id: 'p3',
    name: 'Ana Martínez',
    email: 'ana@innovate.mx',
    company: 'InnovateMX',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Ana',
    challenge: 'Crear un sistema de onboarding automatizado para nuevos empleados',
    mentorId: 'm2',
    progress: 85,
    currentSolutionType: 4,
    badges: ['early_bird', 'first_delivery', 'content_master', 'data_analyst', 'agent_builder'],
    streak: 8,
    daysUntilDemoday: 12,
    canAccessDemoday: true,
    deliverables: [
      {
        id: 'd6',
        name: 'Guía de onboarding interactiva',
        description: 'Contenido gamificado para nuevos empleados',
        solutionType: 1,
        status: 'validated',
        toolUsed: 'chatgpt',
        challengeRelation: 'Material base del onboarding',
        submittedAt: '2024-01-10',
      },
      {
        id: 'd7',
        name: 'Análisis de efectividad de onboarding',
        description: 'Métricas de retención y satisfacción',
        solutionType: 2,
        status: 'validated',
        toolUsed: 'powerbi',
        challengeRelation: 'Medir impacto del sistema',
        submittedAt: '2024-01-14',
      },
      {
        id: 'd8',
        name: 'Bot de bienvenida personalizado',
        description: 'Asistente que guía el primer día',
        solutionType: 3,
        status: 'validated',
        toolUsed: 'chatgpt',
        challengeRelation: 'Automatizar acompañamiento inicial',
        submittedAt: '2024-01-18',
      },
      {
        id: 'd9',
        name: 'Portal de onboarding automatizado',
        description: 'App web que integra todo el proceso',
        solutionType: 4,
        status: 'in_progress',
        toolUsed: 'lovable',
        challengeRelation: 'Solución integral del reto',
      },
    ],
  },
];

export const mockMentors: Mentor[] = [
  {
    id: 'm1',
    name: 'Dr. Roberto Sánchez',
    email: 'roberto@lider-ia.com',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Roberto',
    participantIds: ['p1', 'p2'],
    pendingValidations: 2,
  },
  {
    id: 'm2',
    name: 'Ing. Laura Fernández',
    email: 'laura@lider-ia.com',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Laura',
    participantIds: ['p3'],
    pendingValidations: 0,
  },
];

export const BADGES = {
  early_bird: { name: 'Early Bird', icon: '🐦', description: 'Completó su primer entregable en la primera semana' },
  first_delivery: { name: 'Primera Entrega', icon: '🎯', description: 'Envió su primer entregable' },
  content_master: { name: 'Maestro del Contenido', icon: '✍️', description: 'Completó el módulo de Creación de Contenido' },
  data_analyst: { name: 'Analista de Datos', icon: '📊', description: 'Completó el módulo de Análisis de Datos' },
  agent_builder: { name: 'Constructor de Agentes', icon: '🤖', description: 'Completó el módulo de Asistentes' },
  automation_expert: { name: 'Experto en Automatización', icon: '⚡', description: 'Completó el módulo de Automatización' },
  streak_3: { name: 'En Racha', icon: '🔥', description: '3 días consecutivos entregando' },
  streak_7: { name: 'Imparable', icon: '💪', description: '7 días consecutivos entregando' },
  demoday_ready: { name: 'Listo para Demoday', icon: '🎤', description: 'Completó todos los requisitos' },
};

export const programStats = {
  totalParticipants: 48,
  activeParticipants: 42,
  avgProgress: 58,
  demodayReadyCount: 12,
  atRiskCount: 8,
  totalDeliverables: 156,
  validatedDeliverables: 98,
};

export type CalendarEventType = 'workshop' | 'mentoring' | 'content' | 'project_delivery';

export type CalendarEventStatus = 'upcoming' | 'live' | 'completed';

export interface Expert {
  id: string;
  name: string;
  photo: string;
  profile: string;
  linkedinUrl: string;
}

export interface CalendarEvent {
  id: string;
  title: string;
  description: string;
  type: CalendarEventType;
  date: string;
  startTime: string;
  endTime: string;
  status: CalendarEventStatus;
  week: number; // 1-9 (8 workshops + 1 week for project delivery)
  company?: string;
  meetLink?: string;
  recordingUrl?: string;
  materialUrl?: string;
  mentor?: string;
  expert?: Expert;
  deliverableId?: string;
  deliverableUploaded?: boolean;
  deliverableInstructions?: string;
  deliverableTools?: string[];
}

// Mock Experts Data
export const mockExperts: Expert[] = [
  {
    id: 'exp1',
    name: 'Carlos Mendoza',
    photo: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Carlos',
    profile: 'Especialista en IA Generativa con más de 10 años de experiencia en la industria tech. Ha liderado proyectos de transformación digital en empresas Fortune 500 y es reconocido speaker en conferencias de tecnología.',
    linkedinUrl: 'https://linkedin.com/in/carlosmendoza',
  },
  {
    id: 'exp2',
    name: 'Ana Rodríguez',
    photo: 'https://api.dicebear.com/7.x/avataaars/svg?seed=AnaR',
    profile: 'Data Scientist con expertise en análisis predictivo y visualización de datos. Ex-Google y Amazon, ha entrenado a más de 5,000 profesionales en el uso de herramientas de análisis con IA.',
    linkedinUrl: 'https://linkedin.com/in/anarodriguez',
  },
  {
    id: 'exp3',
    name: 'Diego Vargas',
    photo: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Diego',
    profile: 'Experto en marketing digital y creación de contenido con IA. Fundador de una agencia creativa que ha generado más de 10M de vistas con contenido asistido por IA.',
    linkedinUrl: 'https://linkedin.com/in/diegovargas',
  },
  {
    id: 'exp4',
    name: 'Laura Martínez',
    photo: 'https://api.dicebear.com/7.x/avataaars/svg?seed=LauraM',
    profile: 'Ingeniera de automatización con certificaciones en Make, Zapier y n8n. Ha implementado más de 200 flujos de automatización ahorrando miles de horas de trabajo manual.',
    linkedinUrl: 'https://linkedin.com/in/lauramartinez',
  },
  {
    id: 'exp5',
    name: 'Roberto Silva',
    photo: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Roberto',
    profile: 'Arquitecto de soluciones conversacionales y agentes de IA. Ha diseñado chatbots y asistentes virtuales para bancos, retail y telecomunicaciones en Latinoamérica.',
    linkedinUrl: 'https://linkedin.com/in/robertosilva',
  },
  {
    id: 'exp6',
    name: 'Patricia Gómez',
    photo: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Patricia',
    profile: 'Consultora de innovación y metodologías ágiles. Experta en design thinking aplicado a problemas de negocio y facilitadora de sesiones en vivo de ideación.',
    linkedinUrl: 'https://linkedin.com/in/patriciagomez',
  },
  {
    id: 'exp7',
    name: 'Andrés Castillo',
    photo: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Andres',
    profile: 'Desarrollador full-stack y evangelista de "vibe coding". Pionero en el uso de herramientas como Lovable y Cursor para desarrollo rápido de prototipos funcionales.',
    linkedinUrl: 'https://linkedin.com/in/andrescastillo',
  },
  {
    id: 'exp8',
    name: 'Mariana López',
    photo: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Mariana',
    profile: 'Mentora de startups y especialista en product management. Ha acompañado a más de 100 equipos en el desarrollo y lanzamiento de productos digitales.',
    linkedinUrl: 'https://linkedin.com/in/marianalopez',
  },
];

export const mockCalendarEvents: CalendarEvent[] = [
  // SEMANA 1 - Workshop: Prompting y Vibe Coding
  {
    id: 'ws1',
    title: 'Sesión en Vivo: Prompting y Vibe Coding',
    description: 'Aprende técnicas avanzadas de prompting y los fundamentos del desarrollo asistido por IA',
    type: 'workshop',
    date: '2026-01-07',
    startTime: '10:00',
    endTime: '12:00',
    status: 'completed',
    week: 1,
    recordingUrl: 'https://drive.google.com/recording/ws1',
    materialUrl: 'https://drive.google.com/materials/ws1',
    expert: mockExperts[0],
    deliverableId: 'ws-d1',
    deliverableTools: ['ChatGPT'],
    deliverableUploaded: true,
    deliverableInstructions: `
      <div class="space-y-4">
        <h4 class="font-semibold text-primary">📋 Instrucciones del Entregable</h4>
        <div class="grid gap-3">
          <div class="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p class="font-medium text-red-600 dark:text-red-400 flex items-center gap-2">🎯 Problema concreto</p>
            <p class="text-sm mt-1">Pierdes tiempo respondiendo las mismas preguntas o realizando tareas repetitivas que podrían optimizarse con IA.</p>
          </div>
          <div class="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
            <p class="font-medium text-green-600 dark:text-green-400 flex items-center gap-2">✅ Resultado esperado</p>
            <p class="text-sm mt-1">Documento con 3 prompts optimizados usando la estructura RACE, probados en ChatGPT con capturas de pantalla de los resultados obtenidos.</p>
          </div>
          <div class="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <p class="font-medium text-blue-600 dark:text-blue-400 flex items-center gap-2">⏱️ Tiempo estimado de trabajo</p>
            <p class="text-sm mt-1">2-3 horas</p>
          </div>
        </div>
      </div>
    `,
  },

  // SEMANA 2 - Workshop: Análisis de Datos
  {
    id: 'ws2',
    title: 'Sesión en Vivo: Análisis de Datos',
    description: 'Domina el análisis de datos con herramientas de IA para tomar mejores decisiones',
    type: 'workshop',
    date: '2026-01-14',
    startTime: '10:00',
    endTime: '12:00',
    status: 'completed',
    week: 2,
    recordingUrl: 'https://drive.google.com/recording/ws2',
    materialUrl: 'https://drive.google.com/materials/ws2',
    expert: mockExperts[1],
    deliverableId: 'ws-d2',
    deliverableTools: ['ChatGPT', 'Gemini', 'Power BI'],
    deliverableUploaded: true,
    deliverableInstructions: `
      <div class="space-y-4">
        <h4 class="font-semibold text-primary">📊 Instrucciones del Entregable</h4>
        <div class="grid gap-3">
          <div class="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p class="font-medium text-red-600 dark:text-red-400 flex items-center gap-2">🎯 Problema concreto</p>
            <p class="text-sm mt-1">No tienes visibilidad clara de tus datos clave ni puedes identificar tendencias o patrones para tomar mejores decisiones.</p>
          </div>
          <div class="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
            <p class="font-medium text-green-600 dark:text-green-400 flex items-center gap-2">✅ Resultado esperado</p>
            <p class="text-sm mt-1">Dashboard interactivo con mínimo 3 visualizaciones, filtros interactivos y narrativa inteligente generada con IA.</p>
          </div>
          <div class="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <p class="font-medium text-blue-600 dark:text-blue-400 flex items-center gap-2">⏱️ Tiempo estimado de trabajo</p>
            <p class="text-sm mt-1">4-5 horas</p>
          </div>
        </div>
      </div>
    `,
  },

  // SEMANA 3 - Workshop: Creación de Contenido
  {
    id: 'ws3',
    title: 'Sesión en Vivo: Creación de Contenido',
    description: 'Genera contenido de alto impacto utilizando las mejores herramientas de IA generativa',
    type: 'workshop',
    date: '2026-01-21',
    startTime: '10:00',
    endTime: '12:00',
    status: 'live',
    week: 3,
    meetLink: 'https://meet.google.com/abc-defg-hij',
    materialUrl: 'https://drive.google.com/materials/ws3',
    expert: mockExperts[2],
    deliverableId: 'ws-d3',
    deliverableTools: ['ChatGPT', 'Midjourney', 'Gamma'],
    deliverableUploaded: false,
    deliverableInstructions: `
      <div class="space-y-4">
        <h4 class="font-semibold text-primary">✍️ Instrucciones del Entregable</h4>
        <div class="grid gap-3">
          <div class="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p class="font-medium text-red-600 dark:text-red-400 flex items-center gap-2">🎯 Problema concreto</p>
            <p class="text-sm mt-1">Crear contenido de calidad consume demasiado tiempo y recursos de tu equipo.</p>
          </div>
          <div class="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
            <p class="font-medium text-green-600 dark:text-green-400 flex items-center gap-2">✅ Resultado esperado</p>
            <p class="text-sm mt-1">Pack de contenido con 3 piezas (post, artículo, presentación) generadas con IA y editadas para tu audiencia específica.</p>
          </div>
          <div class="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <p class="font-medium text-blue-600 dark:text-blue-400 flex items-center gap-2">⏱️ Tiempo estimado de trabajo</p>
            <p class="text-sm mt-1">3-4 horas</p>
          </div>
        </div>
      </div>
    `,
  },

  // SEMANA 4 - Workshop: Automatizaciones
  {
    id: 'ws4',
    title: 'Sesión en Vivo: Automatizaciones',
    description: 'Crea flujos de trabajo automatizados sin código usando Make, Zapier y n8n',
    type: 'workshop',
    date: '2026-01-28',
    startTime: '10:00',
    endTime: '12:00',
    status: 'upcoming',
    week: 4,
    meetLink: 'https://meet.google.com/jkl-mnop-qrs',
    expert: mockExperts[3],
    deliverableId: 'ws-d4',
    deliverableTools: ['Make', 'Zapier', 'ChatGPT'],
    deliverableUploaded: false,
    deliverableInstructions: `
      <div class="space-y-4">
        <h4 class="font-semibold text-primary">⚙️ Instrucciones del Entregable</h4>
        <div class="grid gap-3">
          <div class="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p class="font-medium text-red-600 dark:text-red-400 flex items-center gap-2">🎯 Problema concreto</p>
            <p class="text-sm mt-1">Realizas tareas manuales repetitivas entre diferentes herramientas que consumen horas de tu semana laboral.</p>
          </div>
          <div class="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
            <p class="font-medium text-green-600 dark:text-green-400 flex items-center gap-2">✅ Resultado esperado</p>
            <p class="text-sm mt-1">Escenario funcional en Make con mínimo 5 módulos, integración con 2+ herramientas y video explicativo de 2-3 min.</p>
          </div>
          <div class="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <p class="font-medium text-blue-600 dark:text-blue-400 flex items-center gap-2">⏱️ Tiempo estimado de trabajo</p>
            <p class="text-sm mt-1">4-6 horas</p>
          </div>
        </div>
      </div>
    `,
  },

  // SEMANA 5 - Workshop: Agentes IA
  {
    id: 'ws5',
    title: 'Sesión en Vivo: Agentes IA',
    description: 'Construye asistentes y agentes inteligentes que automatizan interacciones',
    type: 'workshop',
    date: '2026-02-04',
    startTime: '10:00',
    endTime: '12:00',
    status: 'upcoming',
    week: 5,
    meetLink: 'https://meet.google.com/tuv-wxyz-123',
    expert: mockExperts[4],
    deliverableId: 'ws-d5',
    deliverableTools: ['ChatGPT', 'Claude'],
    deliverableUploaded: false,
    deliverableInstructions: `
      <div class="space-y-4">
        <h4 class="font-semibold text-primary">🤖 Instrucciones del Entregable</h4>
        <div class="grid gap-3">
          <div class="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p class="font-medium text-red-600 dark:text-red-400 flex items-center gap-2">🎯 Problema concreto</p>
            <p class="text-sm mt-1">Tu equipo o clientes hacen preguntas repetitivas que consumen tiempo valioso.</p>
          </div>
          <div class="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
            <p class="font-medium text-green-600 dark:text-green-400 flex items-center gap-2">✅ Resultado esperado</p>
            <p class="text-sm mt-1">Agente conversacional funcional con mínimo 5 flujos de conversación y documentación de 3 escenarios de prueba.</p>
          </div>
          <div class="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <p class="font-medium text-blue-600 dark:text-blue-400 flex items-center gap-2">⏱️ Tiempo estimado de trabajo</p>
            <p class="text-sm mt-1">5-6 horas</p>
          </div>
        </div>
      </div>
    `,
  },

  // SEMANA 6 - Workshop: Reto y Solución + Mentoría 1
  {
    id: 'ws6',
    title: 'Sesión en Vivo: Reto y Solución',
    description: 'Define claramente tu reto de negocio y diseña una solución integral con IA',
    type: 'workshop',
    date: '2026-02-11',
    startTime: '10:00',
    endTime: '12:00',
    status: 'upcoming',
    week: 6,
    meetLink: 'https://meet.google.com/456-789-abc',
    expert: mockExperts[5],
    deliverableId: 'ws-d6',
    deliverableTools: ['n8n', 'Make', 'Zapier'],
    deliverableUploaded: false,
    deliverableInstructions: `
      <div class="space-y-4">
        <h4 class="font-semibold text-primary">🎯 Instrucciones del Entregable</h4>
        <div class="grid gap-3">
          <div class="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p class="font-medium text-red-600 dark:text-red-400 flex items-center gap-2">🎯 Problema concreto</p>
            <p class="text-sm mt-1">Necesitas estructurar tu proyecto final integrando todos los módulos aprendidos.</p>
          </div>
          <div class="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
            <p class="font-medium text-green-600 dark:text-green-400 flex items-center gap-2">✅ Resultado esperado</p>
            <p class="text-sm mt-1">Documento de diseño con problema, solución propuesta, herramientas a usar y plan de implementación.</p>
          </div>
          <div class="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <p class="font-medium text-blue-600 dark:text-blue-400 flex items-center gap-2">⏱️ Tiempo estimado de trabajo</p>
            <p class="text-sm mt-1">3-4 horas</p>
          </div>
        </div>
      </div>
    `,
  },
  {
    id: 'mt1',
    title: 'Mentoría 1:1 - Revisión de Reto',
    description: 'Sesión individual para revisar y refinar tu definición del reto de negocio',
    type: 'mentoring',
    date: '2026-02-13',
    startTime: '15:00',
    endTime: '16:00',
    status: 'upcoming',
    week: 6,
    mentor: 'Dr. Roberto Sánchez',
    meetLink: 'https://meet.google.com/mt1-session',
  },

  // SEMANA 7 - Workshop: Desarrollo de Proyecto 1 + Mentoría 2
  {
    id: 'ws7',
    title: 'Sesión en Vivo: Desarrollo de Proyecto 1',
    description: 'Primera sesión de desarrollo práctico de tu proyecto final con acompañamiento',
    type: 'workshop',
    date: '2026-02-18',
    startTime: '10:00',
    endTime: '12:00',
    status: 'upcoming',
    week: 7,
    meetLink: 'https://meet.google.com/def-ghi-jkl',
    expert: mockExperts[6],
    deliverableId: 'ws-d7',
    deliverableTools: ['Lovable', 'Bolt', 'Cursor'],
    deliverableUploaded: false,
    deliverableInstructions: `
      <div class="space-y-4">
        <h4 class="font-semibold text-primary">💻 Instrucciones del Entregable</h4>
        <div class="grid gap-3">
          <div class="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p class="font-medium text-red-600 dark:text-red-400 flex items-center gap-2">🎯 Problema concreto</p>
            <p class="text-sm mt-1">Necesitas avanzar en la construcción de tu solución con herramientas de vibe coding.</p>
          </div>
          <div class="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
            <p class="font-medium text-green-600 dark:text-green-400 flex items-center gap-2">✅ Resultado esperado</p>
            <p class="text-sm mt-1">Primera versión funcional del prototipo con al menos 2 funcionalidades core implementadas.</p>
          </div>
          <div class="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <p class="font-medium text-blue-600 dark:text-blue-400 flex items-center gap-2">⏱️ Tiempo estimado de trabajo</p>
            <p class="text-sm mt-1">6-8 horas</p>
          </div>
        </div>
      </div>
    `,
  },
  {
    id: 'mt2',
    title: 'Mentoría 1:1 - Avance de Proyecto',
    description: 'Revisión del avance de tu proyecto y resolución de bloqueos técnicos',
    type: 'mentoring',
    date: '2026-02-20',
    startTime: '15:00',
    endTime: '16:00',
    status: 'upcoming',
    week: 7,
    mentor: 'Dr. Roberto Sánchez',
    meetLink: 'https://meet.google.com/mt2-session',
  },

  // SEMANA 8 - Workshop: Desarrollo de Proyecto 2 + Mentoría 3
  {
    id: 'ws8',
    title: 'Sesión en Vivo: Desarrollo de Proyecto 2',
    description: 'Última sesión de desarrollo para pulir y completar tu proyecto final',
    type: 'workshop',
    date: '2026-02-25',
    startTime: '10:00',
    endTime: '12:00',
    status: 'upcoming',
    week: 8,
    meetLink: 'https://meet.google.com/mno-pqr-stu',
    expert: mockExperts[7],
    deliverableId: 'ws-d8',
    deliverableTools: ['Gamma', 'ChatGPT'],
    deliverableUploaded: false,
    deliverableInstructions: `
      <div class="space-y-4">
        <h4 class="font-semibold text-primary">🚀 Instrucciones del Entregable</h4>
        <div class="grid gap-3">
          <div class="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p class="font-medium text-red-600 dark:text-red-400 flex items-center gap-2">🎯 Problema concreto</p>
            <p class="text-sm mt-1">Debes completar y pulir tu proyecto para la entrega final.</p>
          </div>
          <div class="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
            <p class="font-medium text-green-600 dark:text-green-400 flex items-center gap-2">✅ Resultado esperado</p>
            <p class="text-sm mt-1">Prototipo completo, funcional y documentado listo para presentación.</p>
          </div>
          <div class="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <p class="font-medium text-blue-600 dark:text-blue-400 flex items-center gap-2">⏱️ Tiempo estimado de trabajo</p>
            <p class="text-sm mt-1">8-10 horas</p>
          </div>
        </div>
      </div>
    `,
  },
  {
    id: 'mt3',
    title: 'Mentoría 1:1 - Preparación para Entrega',
    description: 'Última revisión antes de la entrega del proyecto final',
    type: 'mentoring',
    date: '2026-02-27',
    startTime: '15:00',
    endTime: '16:00',
    status: 'upcoming',
    week: 8,
    mentor: 'Dr. Roberto Sánchez',
    meetLink: 'https://meet.google.com/mt3-session',
  },

  // SEMANA 9 - Mentoría Final + Entrega de Proyecto
  {
    id: 'mt4',
    title: 'Mentoría Final - Revisión Integral',
    description: 'Sesión extendida de 2 horas para revisión final y retroalimentación completa',
    type: 'mentoring',
    date: '2026-03-04',
    startTime: '14:00',
    endTime: '16:00',
    status: 'upcoming',
    week: 9,
    mentor: 'Dr. Roberto Sánchez',
    meetLink: 'https://meet.google.com/mt4-final',
  },
  {
    id: 'pd1',
    title: 'Entrega de Proyecto Final',
    description: 'Fecha límite para entregar tu proyecto final y todos los entregables pendientes',
    type: 'project_delivery',
    date: '2026-03-06',
    startTime: '00:00',
    endTime: '23:59',
    status: 'upcoming',
    week: 9,
    deliverableId: 'project-final',
    deliverableUploaded: false,
    deliverableInstructions: `
      <div class="space-y-4">
        <h4 class="font-semibold text-primary">🏆 Entrega Final del Proyecto</h4>
        <div class="grid gap-3">
          <div class="p-3 bg-primary/10 border border-primary/20 rounded-lg">
            <p class="font-medium text-primary flex items-center gap-2">📦 Qué entregar</p>
            <ul class="text-sm mt-2 space-y-1 list-disc list-inside">
              <li>Link al prototipo funcional</li>
              <li>Video demo de 3-5 minutos</li>
              <li>Documentación del proyecto</li>
              <li>Presentación ejecutiva (5 slides)</li>
            </ul>
          </div>
          <div class="p-3 bg-warning/10 border border-warning/20 rounded-lg">
            <p class="font-medium text-warning flex items-center gap-2">⚠️ Importante</p>
            <p class="text-sm mt-1">Esta es la fecha límite. Después de esta fecha no se aceptarán entregas.</p>
          </div>
        </div>
      </div>
    `,
  },
];
